import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import axiosInstance from "../../api/axios";
import Select from "react-select";
import Autocomplete from "./components/autocomplete";
import countries from "./components/countries";
import { useNavigate } from "react-router-dom";
import {
  getUniversitySelection,
  getUnlistedUniversityName,
  universityOptions,
  filterUniversityOption,
  UNIVERSITY_NOT_LISTED,
} from "./university";
import {
  getGraduationYearQuestionLocation,
  getGraduationYearValue,
  getProfileFieldUpdate,
  isGraduationYearQuestion,
  shouldDisplayCycleQuestion,
} from "./profileQuestions";

const PROFILE_NOT_AVAILABLE = "I don’t have it";
const PROFILE_CHOICE_OPTIONS = [
  { value: "provided", label: "I have it" },
  { value: "not_available", label: PROFILE_NOT_AVAILABLE },
];

const getProfileChoice = (value) => {
  if (value === PROFILE_NOT_AVAILABLE) return "not_available";
  if (value) return "provided";
  return "";
};

const ApplicationForm = ({ email }) => {
  // Passing an email will enable View Mode
  const [sections, setSections] = useState([]);
  const [universitySelection, setUniversitySelection] = useState(null);
  const [formData, setFormData] = useState({
    first: "",
    last: "",
    school: "",
    phone_number: "",
    graduation_year: "",
    linkedin_profile_url: "",
    github_profile_url: "",
    sections: [],
    checkboxes: [false, false, false],
    teamcode: "",
  });
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [resumeSubmitted, setResumeSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [resumeMessage, setResumeMessage] = useState("");
  const [linkedinChoice, setLinkedinChoice] = useState("");
  const [githubChoice, setGithubChoice] = useState("");
  const [onTeam, setOnTeam] = useState("");
  const [team, setTeam] = useState("");
  const navigate = useNavigate();
  const teamCodeOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgba(104, 149, 225, 0.28)"
        : state.isFocused
          ? "rgba(255, 195, 157, 0.4)"
          : "#fffdf7",
      color: "#26385f",
      fontSize: "14px",
      ":active": {
        backgroundColor: "rgba(104, 149, 225, 0.34)",
      },
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: "#fffdf7",
      color: "#26385f",
      borderColor: "rgba(52, 69, 117, 0.48)",
      minHeight: "42px",
      borderRadius: "6px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "rgba(191, 208, 255, 0.62)",
      color: "#26385f",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#26385f",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? "#a5445b" : "#344575",
      ":hover": {
        color: "#a5445b",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "rgba(38, 56, 95, 0.62)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#26385f",
    }),
    input: (provided) => ({
      ...provided,
      color: "#26385f",
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      color: "#344575",
      padding: 4,
      cursor: "pointer",
      ":hover": {
        color: "#a22f49",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#fffdf7",
      border: "1.5px solid rgba(52, 69, 117, 0.48)",
      borderRadius: "6px",
      overflow: "hidden",
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: "#fffdf7",
      paddingTop: 0,
      paddingBottom: 0,
    }),
  };
  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  useEffect(() => {
    const fetchQuestionsAndApplication = async () => {
      try {
        const applicationRequestUrl = email
          ? `/api/applications/${email}`
          : "/api/applications/application";

        const [questionsResponse, applicationResponse] = await Promise.all([
          axiosInstance.get("/api/applications/questions"),
          axiosInstance.get(applicationRequestUrl),
        ]);

        const questions = questionsResponse.data;
        const application = applicationResponse.data;

        const initialSections = questions.map((section) => ({
          section_name: section.section_name,
          answers: section.questions.map((question) => ({
            question_id: question.question_id,
            answer: "",
          })),
        }));

        if (application) {
          // console.log("Here #2");

          const applicationSections = initialSections.map((section) => {
            const matchingSection = (application.sections || []).find(
              (appSection) => appSection.section_name === section.section_name
            );
            if (!matchingSection) return section;

            return {
              section_name: section.section_name,
              answers: section.answers.map((answer) => {
                const matchingAnswer = matchingSection.answers.find(
                  (appAnswer) => appAnswer.question_id === answer.question_id
                );
                if (matchingAnswer?.answer) return matchingAnswer;

                if (
                  isGraduationYearQuestion(answer.question_id) &&
                  application.graduation_year
                ) {
                  return {
                    ...answer,
                    answer: application.graduation_year,
                  };
                }

                return matchingAnswer || answer;
              }),
            };
          });

          setUniversitySelection(getUniversitySelection(application.school));
          setFormData({
            first: application.first,
            last: application.last,
            school: application.school,
            phone_number: application.phone_number || "",
            graduation_year: getGraduationYearValue(
              applicationSections,
              application.graduation_year
            ),
            linkedin_profile_url: application.linkedin_profile_url || "",
            github_profile_url: application.github_profile_url || "",
            sections: applicationSections,
            checkboxes: application.checkboxes || [false, false, false],
            teamcode: application.teamcode || "",
          });
          setLinkedinChoice(
            getProfileChoice(application.linkedin_profile_url)
          );
          setGithubChoice(getProfileChoice(application.github_profile_url));

          if (
            application.hasOwnProperty("teamcode") &&
            application.teamcode !== null
          ) {
            setOnTeam("yes");
            setTeam(application.teamcode);
          }

          try {
            const resumeSubmittedUrl = email
              ? `/api/resume/submitted/${email}`
              : "/api/resume/submitted";
            const res = await axiosInstance.get(resumeSubmittedUrl);
            setResumeSubmitted(res.data.submitted);
            if (res.data.submitted) {
              setResumeMessage("Resume previously submitted");
            }
          } catch (error) {
            // console.log("Unable to check Resume status", error)
          }
        } else {
          // console.log("Here we are #3");

          setFormData({
            first: "",
            last: "",
            school: "",
            phone_number: "",
            graduation_year: "",
            linkedin_profile_url: "",
            github_profile_url: "",
            sections: initialSections,
            checkboxes: [false, false, false],
            teamcode: "",
          });
        }

        setSections(questions);
        setLoading(false);
      } catch (error) {
        // console.log(error);
      }
    };

    fetchQuestionsAndApplication();
  }, [email]);

  const handleFileChange = (e) => {
    setResumeSubmitted(false);
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setFile(null);
      setResumeMessage("No resume uploaded.");
    } else if (selectedFile.size <= 2 * 1024 * 1024) {
      setFile(selectedFile);
      setResumeMessage("New resume successfully uploaded");
    } else {
      setFile(null);
      setResumeMessage(
        "Resume is too large. Please upload a file less than 2 MB."
      );
    }
  };

  const handleInputChange = (sectionIndex, questionIndex, value) => {
    const questionId =
      sections[sectionIndex]?.questions[questionIndex]?.question_id;

    setFormData((current) => {
      const updatedSections = current.sections.map(
        (section, currentSectionIndex) => {
          if (currentSectionIndex !== sectionIndex) return section;

          return {
            ...section,
            answers: section.answers.map((answer, currentQuestionIndex) =>
              currentQuestionIndex === questionIndex
                ? { ...answer, answer: value }
                : answer
            ),
          };
        }
      );

      return {
        ...current,
        sections: updatedSections,
        ...getProfileFieldUpdate(questionId, value),
      };
    });
  };

  const handleCheckboxChange = (index) => {
    const updatedCheckboxes = [...formData.checkboxes];
    updatedCheckboxes[index] = !updatedCheckboxes[index];
    setFormData({ ...formData, checkboxes: updatedCheckboxes });
  };

  const handleListChange = (sectionIndex, questionIndex, index, value) => {
    const updatedSections = [...formData.sections];
    let listAnswers =
      updatedSections[sectionIndex].answers[questionIndex].answer.split(",");
    listAnswers[index] = value;
    updatedSections[sectionIndex].answers[questionIndex].answer =
      listAnswers.join(",");
    setFormData({ ...formData, sections: updatedSections });
  };

  const addListEntry = (sectionIndex, questionIndex) => {
    const updatedSections = [...formData.sections];
    let listAnswers =
      updatedSections[sectionIndex].answers[questionIndex].answer.split(",");
    if (listAnswers.length < 3) {
      listAnswers.push("");
      updatedSections[sectionIndex].answers[questionIndex].answer =
        listAnswers.join(",");
      setFormData({ ...formData, sections: updatedSections });
    }
  };

  const removeListEntry = (sectionIndex, questionIndex, index) => {
    const updatedSections = [...formData.sections];
    let listAnswers =
      updatedSections[sectionIndex].answers[questionIndex].answer.split(",");
    listAnswers.splice(index, 1);
    updatedSections[sectionIndex].answers[questionIndex].answer =
      listAnswers.join(",");
    setFormData({ ...formData, sections: updatedSections });
  };

  const handleUniversitySelection = (selectedOption) => {
    setUniversitySelection(selectedOption);
    setFormData((current) => ({
      ...current,
      school: selectedOption?.value || "",
    }));
  };

  const handleUnlistedUniversityChange = (event) => {
    const value = event.target.value;
    setFormData((current) => ({
      ...current,
      school: value || UNIVERSITY_NOT_LISTED,
    }));
  };

  const setCountry = (sectionIndex, questionIndex, value) => {
    handleInputChange(sectionIndex, questionIndex, value);
  };

  const getSingleSelectValue = (options, value) =>
    options.find((option) => option.value === value) || null;

  const handleProfileChoice = (field, setChoice, selectedOption) => {
    const choice = selectedOption?.value || "";
    setChoice(choice);
    setFormData((current) => ({
      ...current,
      [field]:
        choice === "not_available"
          ? PROFILE_NOT_AVAILABLE
          : current[field] === PROFILE_NOT_AVAILABLE
            ? ""
            : current[field],
    }));
  };

  const handleMultiSelectChange = (
    sectionIndex,
    questionIndex,
    selectedOptions
  ) => {
    const updatedSections = [...formData.sections];
    const selectedValues = selectedOptions.map((option) => option.value);
    updatedSections[sectionIndex].answers[questionIndex].answer =
      selectedValues.join(",");
    setFormData({ ...formData, sections: updatedSections });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setErrorMessage("");

    if (!linkedinChoice || !githubChoice) {
      setErrorMessage(
        "Please answer the LinkedIn and GitHub profile questions."
      );
      return;
    }

    if (!file && !resumeSubmitted) {
      setErrorMessage("Please select a valid resume file.");
      return;
    }

    try {
      // console.log(formData);

      await axiosInstance.post("/api/applications/save", formData);

      if (!resumeSubmitted) {
        const resumeData = new FormData();
        resumeData.append("resume", file);
        await axiosInstance.post("/api/resume/upload", resumeData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      await axiosInstance.post("/api/applications/application", formData);
      // console.log(response);
      setMessage("Application submitted successfully");
      setErrorMessage("");
      navigate("/dashboard");
    } catch (error) {
      // console.log(error);
      setErrorMessage(
        error.response?.data?.message || "Error submitting application"
      );
    }
  };

  const handleSave = async () => {
    setMessage("");
    setErrorMessage("");

    // console.log(formData);

    try {
      await axiosInstance.post(
        "/api/applications/save",
        formData
      );

      if (!resumeSubmitted && file) {
        const resumeData = new FormData();
        resumeData.append("resume", file);
        await axiosInstance.post("/api/resume/upload", resumeData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      setMessage("Application saved successfully");
      setErrorMessage("");
      navigate("/dashboard");
    } catch (error) {
      // console.log(error);
      setErrorMessage("Error saving application");
    }
  };

  const isSectionVisible = (conditionalOn) => {
    if (!conditionalOn) return true;
    for (const section of formData.sections) {
      for (const answer of section.answers) {
        if (answer.question_id === conditionalOn) {
          return answer.answer === "yes";
        }
      }
    }
    return false;
  };

  const handleOnTeam = (value) => {
    setOnTeam(value);
    if (value === "yes") {
      const min = 100000; // Minimum 6-digit number
      const max = 99999999; // Maximum 8-digit number
      const id = Math.floor(Math.random() * (max - min + 1)) + min;

      setFormData({ ...formData, teamcode: id });
      setTeam(id);
    } else if (value === "no") {
      setFormData({ ...formData, teamcode: "" });
      setTeam("");
    }
  };

  const handleTeamChange = (e) => {
    setFormData({ ...formData, teamcode: e.target.value });
    setTeam(e.target.value);
  };

  function splitMultiselect(input) {
    // Split the input string by comma
    const parts = input.split(",");

    // Initialize an array to hold the final fragments
    const fragments = [];

    // Iterate through the parts to create fragments based on the rules
    parts.forEach((part, index) => {
      if (index === 0 || !part.startsWith(" ")) {
        // If it's the first element or doesn't start with a space, create a new fragment
        fragments.push(part);
      } else {
        // Otherwise, it's part of the previous fragment, so append it
        fragments[fragments.length - 1] += "," + part;
      }
    });

    // Trim spaces from each fragment
    return fragments.map((fragment) => fragment.trim());
  }

  const displayedSections = sections
    .map((section, sectionIndex) => ({ section, sectionIndex }))
    .sort((a, b) => {
      if (!email) {
        return a.sectionIndex - b.sectionIndex;
      }

      const aHasShort = a.section.questions?.some(
        (question) => question.question_type === "short_answer"
      );
      const bHasShort = b.section.questions?.some(
        (question) => question.question_type === "short_answer"
      );
      const aHasEssay = a.section.questions?.some(
        (question) => question.question_type === "long_answer"
      );
      const bHasEssay = b.section.questions?.some(
        (question) => question.question_type === "long_answer"
      );

      const sectionPriority = (hasShort, hasEssay) => {
        if (hasShort) return 0;
        if (hasEssay) return 1;
        return 2;
      };

      const aPriority = sectionPriority(aHasShort, aHasEssay);
      const bPriority = sectionPriority(bHasShort, bHasEssay);

      if (aPriority === bPriority) {
        return a.sectionIndex - b.sectionIndex;
      }

      return aPriority - bPriority;
    });

  const graduationYearQuestionLocation =
    getGraduationYearQuestionLocation(sections);

  return (
    <div
      className={`apply-form-outer-div ${email ? "apply-form-view-only" : ""}`}
    >
      <form onSubmit={handleSubmit} className="apply-form">
        {!email && (
          <>
            <div className="app-form-div">
              {/* Code of Conduct */}
              <h1 className="section-title">Code of Conduct</h1>
              <div className="coc">
              <p className="coc-p">
                We take inclusivity and safety seriously, and as part of that
                goal, we uphold the community guidelines shared by all MLH
                Member Events. To attend, you must agree to abide by the terms
                of the{" "}
                <a
                  className="coc-a"
                  href="https://github.com/MLH/mlh-policies/blob/main/code-of-conduct.md"
                  target="_blank"
                  rel="noreferrer"
                >
                  MLH Code of Conduct{" "}
                  <FontAwesomeIcon icon={faExternalLinkAlt} />.
                </a>{" "}
                You must also agree to abide by the terms of the{" "}
                <a
                  className="coc-a"
                  href="https://github.com/MLH/mlh-policies/blob/main/contest-terms.md"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  MLH Contest Terms and Conditions{" "}
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>{" "}
                and the{" "}
                <a
                  className="coc-a"
                  href="https://github.com/MLH/mlh-policies/blob/main/privacy-policy.md"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  MLH Privacy Policy{" "}
                  <FontAwesomeIcon icon={faExternalLinkAlt} />.
                </a>{" "}
                Occasional emails from MLH + DEV are optional, as indicated below.
                Since HackPrinceton is a
                Princeton University sponsored event hosted on Princeton's
                campus, the rules detailed in{" "}
                <a
                  className="coc-a"
                  href="https://odus.princeton.edu/community-standards"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Princeton's Community Standards{" "}
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>{" "}
                take precedence for all things involving this hackathon.
              </p>
                <div className="checkbox-container">
                  <input
                    name="first-checkbox"
                    id="mlh-code-of-conduct"
                    type="checkbox"
                    checked={formData.checkboxes[0]}
                    onChange={() => handleCheckboxChange(0)}
                    required
                  />
                  <label className="coc-l" htmlFor="mlh-code-of-conduct">
                    I have read and agree to the{" "}
                    <a className="coc-a" href="https://github.com/MLH/mlh-policies/blob/main/code-of-conduct.md" target="_blank" rel="noopener noreferrer">MLH Code of Conduct</a>.
                    <span className="star"> *</span>
                  </label>
                </div>
                <div className="checkbox-container">
                  <input
                    name="second-checkbox"
                    id="mlh-data-sharing"
                    type="checkbox"
                    checked={formData.checkboxes[1]}
                    onChange={() => handleCheckboxChange(1)}
                    required
                  />
                  <label className="coc-l" htmlFor="mlh-data-sharing">
                    I authorize you to share my application/registration information
                    with Major League Hacking for event administration, ranking, and
                    administration (including the creation of linked accounts on MLH
                    and DEV (<a className="coc-a" href="https://dev.to" target="_blank" rel="noopener noreferrer">dev.to</a>)){" "}
                    in line with the MLH Privacy Policy. I further agree to the terms
                    of both the{" "}
                    <a className="coc-a" href="https://github.com/MLH/mlh-policies/blob/main/contest-terms.md" target="_blank" rel="noopener noreferrer">MLH Contest Terms and Conditions</a>{" "}
                    and the{" "}
                    <a className="coc-a" href="https://github.com/MLH/mlh-policies/blob/main/privacy-policy.md" target="_blank" rel="noopener noreferrer">MLH Privacy Policy</a>.
                    <span className="star"> *</span>
                  </label>
                </div>
                <div className="checkbox-container">
                  <input
                    name="third-checkbox"
                    id="mlh-optional-emails"
                    type="checkbox"
                    checked={formData.checkboxes[2]}
                    onChange={() => handleCheckboxChange(2)}
                  />
                  <label className="coc-l" htmlFor="mlh-optional-emails">
                    I authorize MLH + DEV to send me occasional emails about relevant
                    events, career opportunities, and community announcements.
                  </label>
                </div>
              </div>
            </div>

            <div className="app-form-div">
              <h1 className="section-title">Basic Information</h1>
              <p className="coc-p">
                Applicants must be matriculated in an undergraduate or graduate
                program at a college or university. Students under 18 may apply
                if they meet this requirement.
              </p>
              <div className="app-form-question-div">
                <label className="label">
                  First Name: <label className="star">*</label>
                </label>
                <input
                  type="text"
                  className="short-answer"
                  value={formData.first}
                  onChange={(e) =>
                    setFormData({ ...formData, first: e.target.value })
                  }
                  required
                />
              </div>
              <div className="app-form-question-div">
                <label className="label">
                  Last Name: <label className="star">*</label>
                </label>
                <input
                  type="text"
                  className="short-answer"
                  value={formData.last}
                  onChange={(e) =>
                    setFormData({ ...formData, last: e.target.value })
                  }
                  required
                />
              </div>
              <div className="app-form-question-div">
                <label className="label" htmlFor="university">
                  University: <span className="star">*</span>
                </label>
                <Select
                  inputId="university"
                  options={universityOptions}
                  value={universitySelection}
                  filterOption={filterUniversityOption}
                  onChange={handleUniversitySelection}
                  className="single-select"
                  classNamePrefix="hp-select"
                  styles={customStyles}
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  placeholder="Search for your university"
                  required
                />
                <button
                  type="button"
                  className="university-not-listed-button"
                  aria-expanded={universitySelection?.value === UNIVERSITY_NOT_LISTED}
                  aria-controls="unlisted-university-field"
                  onClick={() => {
                    if (universitySelection?.value !== UNIVERSITY_NOT_LISTED) {
                      handleUniversitySelection(getUniversitySelection(UNIVERSITY_NOT_LISTED));
                    }
                  }}
                >
                  University Not Listed
                </button>
                {universitySelection?.value === UNIVERSITY_NOT_LISTED && (
                  <div id="unlisted-university-field" className="unlisted-university-field">
                    <label className="label" htmlFor="unlisted-university">
                      University Name: <span className="star">*</span>
                    </label>
                    <input
                      id="unlisted-university"
                      type="text"
                      className="short-answer"
                      value={getUnlistedUniversityName(formData.school)}
                      onChange={handleUnlistedUniversityChange}
                      aria-describedby="unlisted-university-help"
                      minLength={2}
                      maxLength={200}
                      required
                    />
                    <p
                      id="unlisted-university-help"
                      className="field-help"
                    >
                      Enter the full name of your university.
                    </p>
                  </div>
                )}
              </div>
              <div className="app-form-question-div">
                <label className="label" htmlFor="phone-number">
                  Phone Number: <span className="star">*</span>
                </label>
                <input
                  id="phone-number"
                  type="tel"
                  className="short-answer"
                  autoComplete="tel"
                  maxLength={30}
                  value={formData.phone_number}
                  onChange={(event) =>
                    setFormData((current) => ({
                      ...current,
                      phone_number: event.target.value,
                    }))
                  }
                  required
                />
              </div>
              {!graduationYearQuestionLocation && (
                <div className="app-form-question-div">
                  <label className="label" htmlFor="graduation-year">
                    Graduation Year: <span className="star">*</span>
                  </label>
                  <input
                    id="graduation-year"
                    type="number"
                    className="short-answer"
                    min="1900"
                    max="2100"
                    inputMode="numeric"
                    value={formData.graduation_year}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        graduation_year: event.target.value,
                      }))
                    }
                    required
                  />
                </div>
              )}
              <div className="app-form-question-div">
                <label className="label" htmlFor="linkedin-profile-choice">
                  LinkedIn Profile: <span className="star">*</span>
                </label>
                <Select
                  inputId="linkedin-profile-choice"
                  options={PROFILE_CHOICE_OPTIONS}
                  value={getSingleSelectValue(
                    PROFILE_CHOICE_OPTIONS,
                    linkedinChoice
                  )}
                  onChange={(selectedOption) =>
                    handleProfileChoice(
                      "linkedin_profile_url",
                      setLinkedinChoice,
                      selectedOption
                    )
                  }
                  className="single-select"
                  classNamePrefix="hp-select"
                  styles={customStyles}
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  placeholder="Please select one"
                  isSearchable={false}
                  aria-required="true"
                />
                {linkedinChoice === "provided" && (
                  <input
                    type="url"
                    className="short-answer"
                    aria-label="LinkedIn profile URL"
                    autoComplete="url"
                    maxLength={300}
                    placeholder="https://www.linkedin.com/in/your-profile"
                    value={formData.linkedin_profile_url}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        linkedin_profile_url: event.target.value,
                      }))
                    }
                    required
                  />
                )}
              </div>
              <div className="app-form-question-div">
                <label className="label" htmlFor="github-profile-choice">
                  GitHub Profile: <span className="star">*</span>
                </label>
                <Select
                  inputId="github-profile-choice"
                  options={PROFILE_CHOICE_OPTIONS}
                  value={getSingleSelectValue(
                    PROFILE_CHOICE_OPTIONS,
                    githubChoice
                  )}
                  onChange={(selectedOption) =>
                    handleProfileChoice(
                      "github_profile_url",
                      setGithubChoice,
                      selectedOption
                    )
                  }
                  className="single-select"
                  classNamePrefix="hp-select"
                  styles={customStyles}
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  placeholder="Please select one"
                  isSearchable={false}
                  aria-required="true"
                />
                {githubChoice === "provided" && (
                  <input
                    type="url"
                    className="short-answer"
                    aria-label="GitHub profile URL"
                    autoComplete="url"
                    maxLength={300}
                    placeholder="https://github.com/your-username"
                    value={formData.github_profile_url}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        github_profile_url: event.target.value,
                      }))
                    }
                    required
                  />
                )}
              </div>
            </div>
          </>
        )}
        {!loading &&
          displayedSections.map(
            ({ section, sectionIndex }) => {
              if (!isSectionVisible(section.conditional_on)) {
                return null;
              }

              const displayedQuestions = section.questions
                .map((question, questionIndex) => ({ question, questionIndex }))
                .filter(({ question, questionIndex }) =>
                  shouldDisplayCycleQuestion(
                    question,
                    sectionIndex,
                    questionIndex,
                    graduationYearQuestionLocation
                  )
                )
                .sort((a, b) => {
                  if (!email) {
                    return a.questionIndex - b.questionIndex;
                  }

                  const aPriority =
                    a.question.question_type === "short_answer" ? 0 : 1;
                  const bPriority =
                    b.question.question_type === "short_answer" ? 0 : 1;

                  if (aPriority === bPriority) {
                    return a.questionIndex - b.questionIndex;
                  }

                  return aPriority - bPriority;
                });

              return (
                <div key={section.section_name} className="app-form-div">
                  <h1 className="section-title">{section.section_name}</h1>
                  {displayedQuestions.map(({ question, questionIndex }) => (
                    <div
                      key={question.question_id}
                      className="app-form-question-div"
                    >
                      {question.question_type === "numeric" && (
                        <>
                          <label className="label">
                            {question.question_text}{" "}
                            <label className="star">*</label>
                          </label>
                          <input
                            type="number"
                            className="numeric"
                            min="16"
                            value={
                              formData.sections[sectionIndex].answers[
                                questionIndex
                              ].answer
                            }
                            onChange={(e) =>
                              handleInputChange(
                                sectionIndex,
                                questionIndex,
                                e.target.value
                              )
                            }
                            required
                          />
                        </>
                      )}
                      {question.question_type === "boolean" && (
                        <>
                          <label className="label">
                            {question.question_text}{" "}
                            <label className="star">*</label>
                          </label>
                          <Select
                            options={[
                              { value: "yes", label: "Yes" },
                              { value: "no", label: "No" },
                            ]}
                            value={getSingleSelectValue(
                              [
                                { value: "yes", label: "Yes" },
                                { value: "no", label: "No" },
                              ],
                              formData.sections[sectionIndex].answers[
                                questionIndex
                              ].answer
                            )}
                            onChange={(selectedOption) =>
                              handleInputChange(
                                sectionIndex,
                                questionIndex,
                                selectedOption?.value || ""
                              )
                            }
                            className="single-select"
                            classNamePrefix="hp-select"
                            styles={customStyles}
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            placeholder="Please select one"
                          />
                        </>
                      )}
                      {question.question_type === "short_answer" && (
                        <>
                          <label className="label">
                            {question.question_text}{" "}
                            <label className="star">*</label>
                          </label>
                          <textarea
                            value={
                              formData.sections[sectionIndex].answers[
                                questionIndex
                              ].answer
                            }
                            className="short-answer"
                            onChange={(e) =>
                              handleInputChange(
                                sectionIndex,
                                questionIndex,
                                e.target.value
                              )
                            }
                            required
                            maxLength={100}
                          />
                        </>
                      )}
                      {question.question_type === "long_answer" && (
                        <>
                          <label className="label">
                            {question.question_text}{" "}
                            <label className="star">*</label>
                          </label>
                          <textarea
                            value={
                              formData.sections[sectionIndex].answers[
                                questionIndex
                              ].answer
                            }
                            className="long-answer"
                            onChange={(e) =>
                              handleInputChange(
                                sectionIndex,
                                questionIndex,
                                e.target.value
                              )
                            }
                            required
                            maxLength={1500}
                          />
                        </>
                      )}
                      {question.question_type === "multiple_choice" && (
                        <>
                          <label className="label">
                            {question.question_text}{" "}
                            <label className="star">*</label>
                          </label>
                          <Select
                            options={question.options.map((option) => ({
                              value: option,
                              label: option,
                            }))}
                            value={getSingleSelectValue(
                              question.options.map((option) => ({
                                value: option,
                                label: option,
                              })),
                              formData.sections[sectionIndex].answers[
                                questionIndex
                              ].answer
                            )}
                            onChange={(selectedOption) =>
                              handleInputChange(
                                sectionIndex,
                                questionIndex,
                                selectedOption?.value || ""
                              )
                            }
                            className="single-select"
                            classNamePrefix="hp-select"
                            styles={customStyles}
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            placeholder="Please select one"
                          />
                        </>
                      )}
                      {question.question_type === "multiselect" && (
                        <>
                          <label className="label">
                            {question.question_text}{" "}
                            <label className="star">*</label>
                          </label>
                          <Select
                            isMulti
                            options={question.options.map((option) => ({
                              value: option,
                              label: option,
                            }))}
                            value={
                              formData.sections[sectionIndex].answers[
                                questionIndex
                              ].answer
                                ? splitMultiselect(
                                    formData.sections[sectionIndex].answers[
                                      questionIndex
                                    ].answer
                                  ).map((value) => ({ value, label: value }))
                                : null
                            }
                            onChange={(selectedOptions) =>
                              handleMultiSelectChange(
                                sectionIndex,
                                questionIndex,
                                selectedOptions
                              )
                            }
                            className="multi-select"
                            classNamePrefix="hp-select"
                            styles={customStyles}
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            required
                          />
                        </>
                      )}
                      {question.question_type === "list" && (
                        <>
                          <label className="label">
                            {question.question_text}
                          </label>
                          {formData.sections[sectionIndex].answers[
                            questionIndex
                          ].answer
                            .split(",")
                            .map((item, index) => (
                              <div key={index}>
                                <input
                                  type="text"
                                  className="list"
                                  value={item}
                                  onChange={(e) =>
                                    handleListChange(
                                      sectionIndex,
                                      questionIndex,
                                      index,
                                      e.target.value
                                    )
                                  }
                                />
                                {index > 0 && (
                                  <button
                                    type="button"
                                    className="list-button"
                                    onClick={() =>
                                      removeListEntry(
                                        sectionIndex,
                                        questionIndex,
                                        index
                                      )
                                    }
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                          {formData.sections[sectionIndex].answers[
                            questionIndex
                          ].answer.split(",").length < 3 && (
                            <button
                              type="button"
                              className="list-button-add"
                              onClick={() =>
                                addListEntry(sectionIndex, questionIndex)
                              }
                            >
                              Add another
                            </button>
                          )}
                        </>
                      )}
                      {question.question_type === "resume" && (
                        <>
                          <label className="label">
                            {question.question_text}{" "}
                            <label className="star">*</label>
                          </label>
                          <input
                            type="file"
                            className="resume-button"
                            onChange={handleFileChange}
                            accept=".pdf"
                          />
                          {resumeMessage && (resumeSubmitted || file) && (
                            <p className="resume-message">{resumeMessage}</p>
                          )}
                          {resumeMessage && !resumeSubmitted && !file && (
                            <p className="resume-error">{resumeMessage}</p>
                          )}
                        </>
                      )}
                      {question.question_type === "country" && (
                        <>
                          <label className="label">
                            {question.question_text}{" "}
                            <label className="star">*</label>
                          </label>
                          <Autocomplete
                            options={countries}
                            answer={
                              formData.sections[sectionIndex].answers[
                                questionIndex
                              ].answer
                            }
                            updateValue={setCountry}
                            sectionIndex={sectionIndex}
                            questionIndex={questionIndex}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              );
            }
          )}

        {!email && (
          <div className="app-form-div">
            <h1 className="section-title">Team Information</h1>
            <div className="app-form-question-div">
              <p className="team-code-desc">
                <i>
                  If you do not have a team code yet, select yes in the box below
                  and receive a new team code for yourself or your team.
                </i>
                <br></br>
                <br></br>
                <i>
                  If someone on your team has already applied, select no and a box
                  will appear to input their team code.
                </i>
                <br></br>
                <br></br>
                <i>
                  If you are editing your application and already have a team
                  code, yes will automatically be selected and your original team
                  code will be displayed.
                </i>
                <br></br>
              </p>
              <label className="label">
                Would you like to generate a team code?
                <label className="star">*</label>
              </label>
              <Select
                options={teamCodeOptions}
                value={getSingleSelectValue(teamCodeOptions, onTeam)}
                onChange={(selectedOption) =>
                  handleOnTeam(selectedOption?.value || "")
                }
                className="single-select"
                classNamePrefix="hp-select"
                styles={customStyles}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                menuPlacement="auto"
                placeholder="Please select one"
              />
              <input
                type="text"
                value={onTeam}
                onChange={() => {}}
                required
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  width: 0,
                  height: 0,
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />

              {onTeam === "yes" && (
                <div className="af-teamcode-div">
                  <label className="label">
                    Your team code is {team}. Please share this code with your
                    teammates.
                  </label>
                </div>
              )}
              {onTeam === "no" && (
                <div className="af-teamcode-div">
                  <label className="label">
                    What team code was provided to your first teammate who
                    applied?<label className="star">*</label>
                  </label>
                  <input
                    value={team}
                    type="number"
                    className="short-answer-tc"
                    onChange={handleTeamChange}
                    required
                  />
                </div>
              )}
              <p style={{ color: "var(--quaternary-color)" }}>
                NOTE: Team codes will be considered during the application
                process, but are NOT a guarantee that everyone on your team will
                be admitted. Team codes are NOT binding, and you are NOT required
                to team with your team code members during the hackathon.
              </p>
            </div>
          </div>
        )}

        {!email && (
          <div className="b-div">
            <p>Review your answers before submitting. Submitted applications cannot be edited.</p>
            <button onClick={handleSave} type="button">
              Save & Continue Later
            </button>
            <button type="submit">Submit Application</button>
          </div>
        )}
        {message && <p className="appform-message">{message}</p>}
        {errorMessage && (
          <p className="appform-message-error">{errorMessage}</p>
        )}
      </form>
    </div>
  );
};

export default ApplicationForm;
